'use client';
import * as React from 'react';
import { Button, Grow, Typography, Fade, CircularProgress } from '@mui/material';

import { EventBus } from '@/shared/utils';
import { CreativeRadio } from '@/shared/ui';

import { OnboardingEventKeys, OnboardingEvents } from '../events';
import Stepper from './elements/stepper';
import Heading from './elements/heading';
import { useOnboarding } from '../hooks/use-onboarding';
import { useOnboardingStore } from '../stores/onboarding-store';

interface LevelSelectionProps {
  //    Definisci qui i tuoi tipi di prop, ad esempio:
  eventBus: EventBus<OnboardingEvents>;
}

const levelOptions = [
  {
    name: 'Sono agli inizi',
    imageSrc: '/assets/images/onboarding/percorso-no-strumento.svg',
    value: 'Sono agli inizi',
  },
  {
    name: 'Ho già esperienza',
    imageSrc: '/assets/images/onboarding/percorso-si-strumento.svg',
    value: 'Ho già esperienza',
  },
];

export const LevelSelection = ({ eventBus }: LevelSelectionProps) => {
  const [selectedValue, setSelectedValue] = React.useState<string>('');
  const { profileSkillLevel } = useOnboarding(eventBus);
  const { isLoading } = useOnboardingStore();

  // Handler for radio button selection
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === levelOptions[1].value) {
      eventBus.emit(OnboardingEventKeys.levelSelection.child.view, undefined);
    }
    eventBus.emit(OnboardingEventKeys.levelSelection.selection.do, {
      skillLevel: event.target.value,
    });
    setSelectedValue(event.target.value);
  };

  React.useEffect(() => {
    if (profileSkillLevel) {
      if (profileSkillLevel.skillLevel !== levelOptions[0].value) {
        eventBus.emit(OnboardingEventKeys.levelSelection.child.view, undefined);
      }
      setSelectedValue(profileSkillLevel.skillLevel);
    }
  }, [profileSkillLevel]);

  const handleEnd = () => {
    // Emettiamo l'evento di end onboarding
    eventBus.emit(OnboardingEventKeys.end.do, undefined);
  };

  return (
    <>
      {/* Stepper */}
      <Fade in={true}>
        <div>
          <Stepper
            activeStep={2}
            onBack={() => eventBus.emit(OnboardingEventKeys.studySessions.view, undefined)}
          />
        </div>
      </Fade>

      <section className="flex flex-col pt-10 pb-12 gap-y-8 items-center max-w-[750px] m-auto text-center">
        {/* Heading */}
        <Fade
          in={true}
          style={{
            transitionDuration: '700ms',
          }}
        >
          <div>
            <Heading
              title="Da dove partiamo?"
              subtitle="Qualunque sia il tuo punto di partenza, su Vibly troverai il percorso fatto apposta per te. Facci sapere come te la cavi con lo strumento e iniziamo!"
            />
          </div>
        </Fade>

        {/* Options to select */}
        <div className="grid grid-cols-2 gap-4 max-w-[650px] w-full">
          {/* CreativeRadio */}
          {levelOptions.map((levelOption) => (
            <Grow
              key={levelOption.value}
              in={true}
              style={{
                transformOrigin: 'center center',
                transitionDelay: '50ms',
              }}
            >
              <div>
                <CreativeRadio
                  imageSrc={levelOption.imageSrc}
                  altText={levelOption.value}
                  name={levelOption.name}
                  value={levelOption.value}
                  checked={selectedValue === levelOption.value}
                  onChange={handleRadioChange}
                />
              </div>
            </Grow>
          ))}
        </div>

        <Grow in={true}>
          <div>
            <Button
              size="large"
              variant="contained"
              color="primary"
              className="max-w-full w-[220px] gap-2"
              disabled={selectedValue === '' || isLoading}
              onClick={handleEnd}
            >
              {isLoading && <CircularProgress size={16} color="inherit" />}
              <Typography variant="button-l">Continua</Typography>
            </Button>
          </div>
        </Grow>
      </section>
    </>
  );
};
